// =============================================================================
// FILE:
// frontend/src/modules/pms/material_logistics/pages/MaterialTraceabilityReportPage.js
// =============================================================================

import React, {
  useEffect,
  useState
} from "react";

import {
  RefreshCw,
  Download,
  Filter
} from "lucide-react";

import TraceabilityKPISection
from "../components/TraceabilityKPISection";

import TraceabilityFilterPanel
from "../components/TraceabilityFilterPanel";

import TraceabilityGrid
from "../components/TraceabilityGrid";

import {
  getMaterialTraceabilityReport
} from "../services/materialTraceabilityService";

import {
  getProjects
} from "../../task_material/services/materialReservationService";

// =============================================================================
// COMPONENT
// =============================================================================

export default function MaterialTraceabilityReportPage() {

  // ===========================================================================
  // STATE
  // ===========================================================================
  const [filters, setFilters] =
    useState({

      search: "",

      status: "",

      validation_status: "",

      project_id: "",

      from_date: "",

      to_date: ""
    });


  const [loading, setLoading] =
    useState(false);


  const [rows, setRows] =
    useState([]);


  const [kpis, setKpis] =
    useState({});

    const [projects, setProjects] =
  useState([]);



  // ===========================================================================
  // FILTER CHANGE
  // ===========================================================================
  const handleFilterChange = (
    field,
    value
  ) => {

    setFilters((prev) => ({

      ...prev,

      [field]: value
    }));
  };


  const loadProjects = async () => {

  try {

    const response =
      await getProjects();

    console.log(
      "PROJECT API RESPONSE",
      response
    );

    if (response.success) {

      setProjects(
        response.data || []
      );
    }

  } catch (err) {

    console.error(
      "Failed to load projects",
      err
    );
  }
};

  // ===========================================================================
  // LOAD REPORT
  // ===========================================================================
  const loadReport = async () => {

    try {

      setLoading(true);

      const response =
        await getMaterialTraceabilityReport(
          filters
        );

      if (response?.success) {

        setRows(
          response.data?.rows || []
        );

        setKpis(
          response.data?.kpis || {}
        );

      } else {

        setRows([]);

        setKpis({});
      }

    } catch (err) {

      console.error(
        "Failed to load traceability report",
        err
      );

      // -----------------------------------------------------------------------
      // TEMP FALLBACK MOCK DATA
      // -----------------------------------------------------------------------
      setRows([
        {
          id: 1,

          project_name:
            "Receiver Vessel",

          phase_name:
            "Fabrication",

          task_name:
            "Vessel Assembly",

          material_code:
            "RM-001",

          material_name:
            "MS Plate 10mm",

          planned_qty: 10,

          reserved_qty: 10,

          dispatched_qty: 10,

          received_qty: 10,

          reconciled_qty: 8,

          pending_dispatch_qty: 0,

          pending_receipt_qty: 0,

          pending_reconciliation_qty: 2,

          validation_status:
            "OK",

          dispatch_status:
            "RECEIVED",

          receipt_status:
            "RECONCILED",

          dispatched_box_count: 5,

          received_box_count: 5,

          damaged_box_count: 0,

          short_box_count: 0
        }
      ]);

      setKpis({

        planned_qty: 10,

        reserved_qty: 10,

        dispatched_qty: 10,

        received_qty: 10,

        reconciled_qty: 8,

        pending_dispatch_qty: 0,

        pending_receipt_qty: 0,

        pending_reconciliation_qty: 2
      });

    } finally {

      setLoading(false);
    }
  };


  // ===========================================================================
  // INITIAL LOAD
  // ===========================================================================
  useEffect(() => {

  loadProjects();

  loadReport();

}, []);


  // ===========================================================================
  // APPLY FILTER
  // ===========================================================================
  const handleApplyFilters =
    () => {

      loadReport();
    };


  // ===========================================================================
  // REFRESH
  // ===========================================================================
  const handleRefresh =
    () => {

      loadReport();
    };


  // ===========================================================================
  // EXPORT
  // ===========================================================================
  const handleExport =
    () => {

      console.log(
        "Export Traceability Report"
      );
    };


  // ===========================================================================
  // RENDER
  // ===========================================================================
  return (

    <div style={styles.page}>


      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <div style={styles.headerCard}>


        <div>

          <div style={styles.headerTitle}>

            Material Traceability Report

          </div>

          <div style={styles.headerSubtitle}>

            End-to-end material lifecycle
            visibility and operational tracking

          </div>

        </div>


        <div style={styles.headerActions}>


          <button
            style={styles.actionBtn}
            onClick={handleRefresh}
          >

            <RefreshCw size={16} />

            Refresh

          </button>


          <button style={styles.actionBtn}>

            <Filter size={16} />

            Advanced Filter

          </button>


          <button
            style={styles.exportBtn}
            onClick={handleExport}
          >

            <Download size={16} />

            Export Excel

          </button>

        </div>

      </div>


      {/* ================================================================ */}
      {/* KPI */}
      {/* ================================================================ */}
      <TraceabilityKPISection
        kpis={kpis}
      />


      {/* ================================================================ */}
      {/* FILTER PANEL */}
      {/* ================================================================ */}
      <TraceabilityFilterPanel

        filters={filters}

        projects={projects}

        onChange={handleFilterChange}

        onApply={handleApplyFilters}
        />


      {/* ================================================================ */}
      {/* GRID */}
      {/* ================================================================ */}
      <TraceabilityGrid
        rows={rows}
        loading={loading}
      />

    </div>
  );
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  page: {

    padding: 20,

    background: "#f5f7fb",

    minHeight: "100vh"
  },

  headerCard: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 20,

    marginBottom: 20,

    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  headerTitle: {

    fontSize: 24,

    fontWeight: 700,

    color: "#111827"
  },

  headerSubtitle: {

    marginTop: 6,

    fontSize: 14,

    color: "#6b7280"
  },

  headerActions: {

    display: "flex",

    gap: 12
  },

  actionBtn: {

    height: 40,

    border: "1px solid #d1d5db",

    background: "#fff",

    borderRadius: 8,

    padding: "0 14px",

    display: "flex",

    alignItems: "center",

    gap: 8,

    cursor: "pointer"
  },

  exportBtn: {

    height: 40,

    border: "none",

    background: "#16a34a",

    color: "#fff",

    borderRadius: 8,

    padding: "0 16px",

    display: "flex",

    alignItems: "center",

    gap: 8,

    cursor: "pointer",

    fontWeight: 600
  }
};